import { atom, useAtomValue } from "jotai";
import { type JSX, useEffect, useRef, useState } from "react";

import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from "@frontend/components/atoms/Autocomplete";
import { useLingui } from "@lingui/react/macro";

import { useAddressText } from "../hooks/useAddressText";
import { addressApi } from "../services";
import type { AddressFieldProps, AddressTextField } from "../types";
import type { AddressCity } from "../types";
import { FieldFrame } from "./FieldFrame";

const DEBOUNCE_MS = 300;

const NO_COUNTRY = atom<string | null | undefined>("");

export type CityFieldProps = AddressFieldProps & {
  /** Narrows the search to one country, and is read as it changes. */
  countryField?: AddressTextField;
  onCitySelected?: (city: AddressCity) => void;
  onCityCleared?: () => void;
};

/** The commune, completed from the referential and typed freely otherwise. */
export function CityField({
  field,
  countryField,
  label,
  placeholder,
  size,
  disabled,
  onCitySelected,
  onCityCleared,
}: CityFieldProps): JSX.Element {
  const { t } = useLingui();

  const { value: city, setValue: setCity, error } = useAddressText(field);

  const country = useAtomValue(countryField?.atom ?? NO_COUNTRY) ?? "";

  const highlighted = useRef<AddressCity | null>(null);

  const [suggestions, setSuggestions] = useState<AddressCity[]>([]);

  const query = city.trim();

  useEffect(() => {
    if ("" === query) {
      return;
    }

    const controller = new AbortController();

    const timeout = setTimeout(() => {
      addressApi
        .searchCities(
          { q: query, countryIso2: country || undefined },
          { signal: controller.signal },
        )
        .then(setSuggestions)
        .catch(() => {
          // Aborted or unanswered: the field stays a plain text one.
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, country]);

  return (
    <FieldFrame label={label} error={error}>
      <Autocomplete<AddressCity>
        items={"" === query ? [] : suggestions}
        value={city}
        size={size}
        disabled={disabled}
        filter={null}
        itemToStringValue={(item) => item.name}
        onItemHighlighted={(item) => {
          highlighted.current = item ?? null;
        }}
        onValueChange={(next, details) => {
          setCity(next);

          if ("item-press" === details.reason && highlighted.current) {
            onCitySelected?.(highlighted.current);

            return;
          }

          if ("" === next) {
            onCityCleared?.();
          }
        }}
      >
        <AutocompleteInput placeholder={placeholder ?? t`City`} />

        <AutocompleteContent>
          <AutocompleteEmpty>{t`No results found.`}</AutocompleteEmpty>

          <AutocompleteList>
            {(item: AddressCity) => (
              <AutocompleteItem key={item.id} value={item}>
                {item.postalCode
                  ? `${item.name} (${item.postalCode})`
                  : item.name}
              </AutocompleteItem>
            )}
          </AutocompleteList>
        </AutocompleteContent>
      </Autocomplete>
    </FieldFrame>
  );
}
